import { OrgConfigResponse } from 'src/services/apiInterfaces';

const DAY_MS = 86400000;

export const ESCALATION_THRESHOLD_DAYS = 7;

export type TrialBannerTone = 'active' | 'expiring' | 'ended';

export interface TrialBanner {
  copyKey: 'active' | 'endsToday' | 'expiring' | 'ended';
  daysLeft: number;
  endDate: string | null;
  isDismissable: boolean;
  tone: TrialBannerTone;
}

// Never local-converted: the expiry is a UTC instant, so a UTC-midnight expiry
// rendered in a US timezone would show the previous day.
const formatUtcDate = (timestamp: number): string =>
  new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    year: 'numeric',
  }).format(timestamp);

const isSameUtcDay = (a: number, b: number): boolean =>
  new Date(a).toISOString().slice(0, 10) ===
  new Date(b).toISOString().slice(0, 10);

const ENDED: TrialBanner = {
  copyKey: 'ended',
  daysLeft: 0,
  endDate: null,
  isDismissable: false,
  tone: 'ended',
};

/**
 * Resolve what the trial banner should say, or `null` for no banner.
 *
 * The server has already decided whether this org is in a trial at all; the
 * only thing left here is how urgent it is, which has to be recomputed
 * client-side because a session can outlive a threshold.
 */
export const resolveTrialBanner = (
  orgConfig:
    | Pick<OrgConfigResponse, 'trial_expires_at' | 'trial_status'>
    | undefined,
  now: number,
): TrialBanner | null => {
  if (!orgConfig) {
    return null;
  }
  if (orgConfig.trial_status === 'trial_ended') {
    return ENDED;
  }
  if (orgConfig.trial_status !== 'trial' || !orgConfig.trial_expires_at) {
    return null;
  }

  const expiresAt = Date.parse(orgConfig.trial_expires_at);
  if (Number.isNaN(expiresAt)) {
    return null;
  }

  // Past the expiry with no `cancelled` event yet: hold "ends today" for a day
  // before degrading, rather than persisting an end BIG hasn't confirmed.
  if (now >= expiresAt + DAY_MS) {
    return ENDED;
  }

  const endDate = formatUtcDate(expiresAt);
  if (now >= expiresAt || isSameUtcDay(now, expiresAt)) {
    return {
      copyKey: 'endsToday',
      daysLeft: 0,
      endDate,
      isDismissable: false,
      tone: 'expiring',
    };
  }

  const daysLeft = Math.ceil((expiresAt - now) / DAY_MS);
  if (daysLeft <= ESCALATION_THRESHOLD_DAYS) {
    return {
      copyKey: 'expiring',
      daysLeft,
      endDate,
      isDismissable: false,
      tone: 'expiring',
    };
  }

  return {
    copyKey: 'active',
    daysLeft,
    endDate,
    isDismissable: true,
    tone: 'active',
  };
};
